/**
 * Toast Notification Service
 */

class ToastService {
  constructor() {
    this.container = null;
    this.toasts = [];
    this.maxToasts = 3;
    this.init();
  }

  init() {
    if (this.container) return;

    this.container = document.createElement("div");
    this.container.className = "toast-container";
    document.body.appendChild(this.container);
  }

  /**
   * @param {string} message
   * @param {string} type - 'success', 'error', 'warning', 'info'
   * @param {number} duration - Duration in ms (0 = permanent)
   */
  show(message, type = "info", duration = 5000) {
    if (this.toasts.length >= this.maxToasts) {
      this.remove(this.toasts[0]);
    }

    const toast = document.createElement("div");
    toast.className = `toast toast-${type}`;

    const icons = {
      success: "✓",
      error: "✕",
      warning: "⚠",
      info: "ℹ"
    };

    toast.innerHTML = `
      <span class="toast-icon">${icons[type] || icons.info}</span>
      <span class="toast-message">${this.escapeHtml(message)}</span>
      <button class="toast-close" aria-label="Fermer">×</button>
    `;

    toast.querySelector(".toast-close").addEventListener("click", () => {
      this.remove(toast);
    });

    this.container.appendChild(toast);
    this.toasts.push(toast);

    if (duration > 0) {
      setTimeout(() => this.remove(toast), duration);
    }
  }

  remove(toast) {
    if (!toast || !toast.parentElement) return;

    toast.classList.add("removing");

    setTimeout(() => {
      if (toast.parentElement) {
        toast.parentElement.removeChild(toast);
      }
      this.toasts = this.toasts.filter((t) => t !== toast);
    }, 300);
  }

  success(message, duration = 5000) {
    this.show(message, "success", duration);
  }

  error(message, duration = 7000) {
    this.show(message, "error", duration);
  }

  warning(message, duration = 6000) {
    this.show(message, "warning", duration);
  }

  info(message, duration = 5000) {
    this.show(message, "info", duration);
  }

  /**
   * Toast de confirmation pour une action destructive (révocation mandat,
   * suppression, etc.). Affiche le message + deux boutons Confirmer/Annuler.
   * Ne s'auto-ferme pas — l'utilisateur doit choisir explicitement.
   *
   * @param {string} message - Question / avertissement.
   * @param {object} [opts]
   * @param {string} [opts.confirmLabel="Confirmer"]
   * @param {string} [opts.cancelLabel="Annuler"]
   * @returns {Promise<boolean>} true si l'utilisateur confirme, false sinon.
   */
  confirm(message, opts = {}) {
    return new Promise((resolve) => {
      if (this.toasts.length >= this.maxToasts) {
        this.remove(this.toasts[0]);
      }

      const confirmLabel = opts.confirmLabel || "Confirmer";
      const cancelLabel = opts.cancelLabel || "Annuler";

      const toast = document.createElement("div");
      toast.className = "toast toast-confirm";
      toast.setAttribute("role", "alertdialog");
      toast.setAttribute("aria-live", "assertive");

      const head = document.createElement("div");
      head.className = "toast-confirm-head";

      const icon = document.createElement("span");
      icon.className = "toast-icon";
      icon.setAttribute("aria-hidden", "true");
      icon.textContent = "⚠";

      const msgSpan = document.createElement("span");
      msgSpan.className = "toast-message";
      msgSpan.textContent = message;

      const closeBtn = document.createElement("button");
      closeBtn.className = "toast-close";
      closeBtn.setAttribute("aria-label", "Fermer");
      closeBtn.textContent = "×";

      head.append(icon, msgSpan, closeBtn);

      const actions = document.createElement("div");
      actions.className = "toast-actions";

      const cancelBtn = document.createElement("button");
      cancelBtn.type = "button";
      cancelBtn.className = "toast-action toast-action--cancel";
      cancelBtn.textContent = cancelLabel;

      const confirmBtn = document.createElement("button");
      confirmBtn.type = "button";
      confirmBtn.className = "toast-action toast-action--confirm";
      confirmBtn.textContent = confirmLabel;

      actions.append(cancelBtn, confirmBtn);
      toast.append(head, actions);

      let settled = false;
      const settle = (value) => {
        if (settled) return;
        settled = true;
        this.remove(toast);
        resolve(value);
      };

      toast.querySelector(".toast-close").addEventListener("click", () => settle(false));
      cancelBtn.addEventListener("click", () => settle(false));
      confirmBtn.addEventListener("click", () => settle(true));

      this.container.appendChild(toast);
      this.toasts.push(toast);

      confirmBtn.focus();
    });
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

const toast = new ToastService();
export default toast;
